import _ from 'lodash';

import {
  canadianProvincesMap,
  JamaicanParishesMap,
  saintVincentAndTheGrenadinesParishesMap,
} from '../constants/geography';
import {
  stateComplianceRules,
  canadaDefaults,
  jamaicanDefaults,
  saintVincentAndTheGrenadinesDefaults,
} from '../constants/compliance';

const OVERRIDE_KEYS = ['stateComplianceRules', 'canadaDefaults', 'jamaicanDefaults', 'saintVincentAndTheGrenadinesDefaults'];

// The statically-bundled config is always retained as the legal safety fallback.
const staticConfig = {
  stateComplianceRules,
  canadaDefaults,
  jamaicanDefaults,
  saintVincentAndTheGrenadinesDefaults,
};

// Populated by the remote-config loaders (API service / browser bootstrap) when the
// remote compliance config is enabled and successfully loaded. Null means "use static".
// IMPORTANT: this is process-global state — set once at load/poll time, never per-request —
// so a single value is shared safely across all concurrent requests in the API process.
let overrideConfig = null;

/**
 * Applies a remote compliance config override. Each of the four maps independently falls
 * back to the statically-bundled config when the override omits it.
 *
 * @param {{
 *   stateComplianceRules?: object,
 *   canadaDefaults?: object,
 *   jamaicanDefaults?: object,
 *   saintVincentAndTheGrenadinesDefaults?: object,
 * }} config
 * @returns {boolean} true if applied; false if the shape was invalid (previous config kept)
 */
export const setComplianceConfigOverride = (config) => {
  // Guard against malformed remote payloads (e.g. bad JSON/module from S3). Without this a
  // typo'd shape would look "set" but silently fall back to static for every map.
  const isValid = _.isPlainObject(config) && _.some(OVERRIDE_KEYS, (key) => _.isPlainObject(config[key]));
  if (!isValid) {
    return false;
  }
  overrideConfig = config;
  return true;
};

export const clearComplianceConfigOverride = () => {
  overrideConfig = null;
};

// Resolves the four config maps, preferring the override per-key and falling back to
// the static value for any map the override omits.
const getActiveConfig = () => {
  if (!overrideConfig) {
    return staticConfig;
  }
  return {
    stateComplianceRules: overrideConfig.stateComplianceRules ?? staticConfig.stateComplianceRules,
    canadaDefaults: overrideConfig.canadaDefaults ?? staticConfig.canadaDefaults,
    jamaicanDefaults: overrideConfig.jamaicanDefaults ?? staticConfig.jamaicanDefaults,
    saintVincentAndTheGrenadinesDefaults:
      overrideConfig.saintVincentAndTheGrenadinesDefaults ?? staticConfig.saintVincentAndTheGrenadinesDefaults,
  };
};

const hasComplianceConfigForStateAndKey = (state, key) => {
  const {
    stateComplianceRules: activeStateComplianceRules,
    canadaDefaults: activeCanadaDefaults,
    jamaicanDefaults: activeJamaicanDefaults,
    saintVincentAndTheGrenadinesDefaults: activeSaintVincentAndTheGrenadinesDefaults,
  } = getActiveConfig();

  if (_.has(activeStateComplianceRules, state)) return _.has(activeStateComplianceRules, `${state}.${key}`);
  if (canadianProvincesMap[state]) return _.has(activeCanadaDefaults, key);
  if (_.includes(_.values(JamaicanParishesMap), state)) return _.has(activeJamaicanDefaults, key);
  if (_.includes(_.keys(saintVincentAndTheGrenadinesParishesMap), state))
    return _.has(activeSaintVincentAndTheGrenadinesDefaults, key);
  return false;
};

export const hasPurchaseLimitConfigForState = (state) => hasComplianceConfigForStateAndKey(state, 'limits');

export const getComplianceConfig = (state, key) => {
  const {
    stateComplianceRules: activeStateComplianceRules,
    canadaDefaults: activeCanadaDefaults,
    jamaicanDefaults: activeJamaicanDefaults,
    saintVincentAndTheGrenadinesDefaults: activeSaintVincentAndTheGrenadinesDefaults,
  } = getActiveConfig();

  // all of canada has the same compliance info except for Alberta and Ontario
  const stateHasConfig = _.includes(_.keys(activeStateComplianceRules), state);
  if (!stateHasConfig && canadianProvincesMap[state]) {
    return _.get(activeCanadaDefaults, key, _.get(activeStateComplianceRules.DEFAULT, key));
  }

  // Jamaica
  // we only have one jamaican dispo currently!
  if (!stateHasConfig && _.includes(_.values(JamaicanParishesMap), state)) {
    return _.get(activeJamaicanDefaults, key, _.get(activeStateComplianceRules.DEFAULT, key));
  }

  // Saint Vincent and the Grenadines
  if (!stateHasConfig && _.includes(_.keys(saintVincentAndTheGrenadinesParishesMap), state)) {
    return _.get(activeSaintVincentAndTheGrenadinesDefaults, key, _.get(activeStateComplianceRules.DEFAULT, key));
  }

  return _.get(activeStateComplianceRules, `${state}.${key}`, _.get(activeStateComplianceRules.DEFAULT, key));
};
